import { useEffect, useRef } from "react";

/**
 * Pagination sentinel observed against the main viewport (page scrollbar).
 * Fires once when it enters view; must leave the viewport before it can
 * fire again (prevents continuous loads while sitting at the page end).
 */
const ColumnPaginationSentinel = ({
    status,
    direction = "next",
    disabled = false,
    loading = false,
    onVisible,
    label,
}) => {
    const sentinelRef = useRef(null);
    const onVisibleRef = useRef(onVisible);
    const armedRef = useRef(true);

    useEffect(() => {
        onVisibleRef.current = onVisible;
    }, [onVisible]);

    useEffect(() => {
        if (disabled) {
            armedRef.current = true;
        }
    }, [disabled]);

    useEffect(() => {
        const node = sentinelRef.current;

        if (!node || disabled || loading) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];

                if (!entry) {
                    return;
                }

                if (entry.isIntersecting) {
                    if (!armedRef.current) {
                        return;
                    }

                    armedRef.current = false;
                    onVisibleRef.current?.();
                    return;
                }

                // Left the viewport — allow the next enter to trigger a load.
                armedRef.current = true;
            },
            {
                // null root = browser viewport / main page scroll
                root: null,
                rootMargin: direction === "next" ? "80px 0px" : "0px 0px 80px 0px",
                threshold: 0,
            }
        );

        observer.observe(node);

        return () => {
            observer.disconnect();
        };
    }, [disabled, loading, direction]);

    return (
        <div
            ref={sentinelRef}
            id={`kanban-sentinel-${direction}-${status}`}
            data-kanban-status={status}
            data-kanban-direction={direction}
            className={`kanban-column-sentinel kanban-column-sentinel--${direction}`}
        >
            {loading ? (
                <div className="kanban-column-sentinel__loading">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">{label || "Loading"}</span>
                    </div>
                </div>
            ) : (
                <div className="kanban-column-sentinel__spacer" />
            )}
        </div>
    );
};

export default ColumnPaginationSentinel;
